/**
 * Self-contained data layer for the Listings tab.
 *
 * Deliberately does NOT go through the Redux `property` slice: the buy/rent and
 * addToListing thunks write shared state fields (`listProperties`, …) that the
 * live My-Properties page also reads, so driving this new tab through them could
 * clobber that page's data. Here we call the same endpoints via the app's
 * configured axios instance and keep the results in the tab's own local state —
 * purely additive, zero impact on existing screens.
 */
import axios from "axios";
import {
  AxiosPropertyInstance,
  AxiosPaymentInstance,
} from "../../../common/utils/AxiosInstance";
import API_BASE_URI from "../../../config/api.config";

// Bank auctions live under the `/api/property` prefix (so the API gateway routes
// them to the property service). That service auth-gates the whole prefix, so we
// send the user's token like every other property call — the browse is
// login-gated on the client. Plain axios (not the guarded instance) since the
// path is its own, but we still attach authHeader().
const AUCTIONS_BASE = `${API_BASE_URI}/api/property/auctions`;

const authHeader = () => {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("token") || "null");
  } catch {
    token = null;
  }
  return token ? { headers: { "x-auth-token": token } } : {};
};

const unwrap = (res) => ({
  items: res?.data?.data?.items ?? [],
  count: res?.data?.data?.count ?? 0,
});

/**
 * LISTED buy/rent inventory near a location (same endpoint the dormant buy/rent
 * pages used). `listedFor` is "sell" (Buy) or "rent" (Rent).
 */
export async function fetchBuyRent({
  listedFor,
  latitude,
  longitude,
  radius,
  textSearch = "",
  categoryKey = "",
  typeKey = "",
  pageIndex = 1,
  pageSize = 24,
}) {
  const body = {
    listed: true,
    listedFor,
    latitude,
    longitude,
    radius,
    textSearch,
    categoryKey,
    typeKey,
  };
  const res = await AxiosPropertyInstance.post(
    `/search/buy-rent?pageIndex=${pageIndex}&pageSize=${pageSize}`,
    body,
    authHeader()
  );
  return unwrap(res);
}

/**
 * The signed-in user's OWN listed properties ("Mine" filter). User-scoped, so it
 * needs a session; opened in owner mode downstream.
 */
export async function fetchMyListings({ pageIndex = 1, pageSize = 24 } = {}) {
  const res = await AxiosPropertyInstance.post(
    `/search/addToListing?pageIndex=${pageIndex}&pageSize=${pageSize}`,
    // includeUnlisted → active AND unlisted-but-previously-listed, so an unlisted
    // property stays in "My listings" with a Relist action instead of vanishing.
    { listed: true, includeUnlisted: true },
    authHeader()
  );
  return unwrap(res);
}

/**
 * The signed-in user's OWN properties that are NOT yet listed — the pool the
 * "List property" flow picks from. Same endpoint, `listed:false`. Supports
 * server-side `textSearch` + pagination (the mobile picker uses the same
 * `{ textSearch, listed }` body + pageIndex/pageSize). Returns `{ items, count }`.
 */
export async function fetchUnlistedProperties({
  textSearch = "",
  pageIndex = 1,
  pageSize = 20,
} = {}) {
  const res = await AxiosPropertyInstance.post(
    `/search/addToListing?pageIndex=${pageIndex}&pageSize=${pageSize}`,
    // draftsOnly → never-listed drafts only, so an unlisted property (which now
    // keeps its sell/rent listedFor) doesn't reappear in the "list a property" pool.
    { listed: false, draftsOnly: true, textSearch },
    authHeader()
  );
  return unwrap(res);
}

/**
 * EVERY property the signed-in user owns, for the "My Properties" tab: the
 * listed + unlisted pool (includeUnlisted) and the never-listed drafts
 * (draftsOnly) together cover all of them. Newest first, deduped.
 */
export async function fetchOwnedProperties() {
  const [listed, drafts] = await Promise.all([
    fetchMyListings({ pageSize: 200 }),
    fetchUnlistedProperties({ pageSize: 200 }),
  ]);
  const seen = new Set();
  return [...listed.items, ...drafts.items]
    .filter((p) => {
      const id = String(p?._id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
}

/**
 * A single property for the detail screen. `owner` uses the auth-scoped
 * `/{id}` (full data for editing); otherwise the read-only public `/{id}/public`
 * so a buyer/renter never needs a session. Returns the property object or null.
 */
export async function fetchListing(id, { owner = false } = {}) {
  const res = await AxiosPropertyInstance.get(
    owner ? `/${id}` : `/${id}/public`,
    authHeader()
  );
  return res?.data?.data?.item ?? res?.data?.data ?? null;
}

// Owner opt-in: make a VERIFIED document purchasable by buyers (owner earns 20%).
export async function setDocSellable(propertyId, docId, sellable) {
  const res = await AxiosPropertyInstance.post(
    `/${propertyId}/docs/${docId}/sellable`,
    { sellable },
    authHeader()
  );
  return res?.data?.data?.item ?? null;
}

// Buyer buys an instant copy of an owner's sellable verified document — the
// existing file is delivered to their order history immediately (no form).
// A short wallet comes back as a Cashfree checkout ({ sessionId, orderId, ... })
// for the rest; the copy is delivered once paid.
export async function buyPropertyDoc(propertyId, docId, documentName) {
  const res = await AxiosPropertyInstance.post(
    `/${propertyId}/buy-doc/${docId}`,
    { documentName, payShortfall: true },
    authHeader()
  );
  return res?.data?.data?.item ?? null;
}

// The credits price a "get a copy" purchase will charge (resolveDocPrice on the
// server — the static catalogue price can be stale, so we never compute it
// client-side). For the buy-confirm dialog. Returns a number, or null on any
// failure so the dialog degrades to its generic "charged to your credits" copy.
export async function fetchDocCopyPrice(propertyId, docId) {
  try {
    const res = await AxiosPropertyInstance.get(
      `/${propertyId}/buy-doc/${docId}/price`,
      authHeader()
    );
    const c = res?.data?.data?.item?.credits;
    return typeof c === "number" ? c : null;
  } catch {
    return null;
  }
}

/**
 * The dynamic listing-form schema for a property's category/subcategory
 * (`static/rent-sell/{category}/{subCategory}/index.json`). Returns
 * `{ fields, sell, rent, other, conversion }` or null.
 */
export async function fetchListingSchema(category, subCategory) {
  const res = await AxiosPropertyInstance.get(
    `static/rent-sell/${category}/${subCategory}/index.json`
  );
  return res?.data ?? null;
}

/**
 * The document catalogue for a property's state + type — the SAME
 * `…/property/docs.json` the My-Properties Documents tab loads. Each entry is
 * `{ id, name, priority, request, credits, url, description, link }`. Driving the
 * owner cross-sell off this (instead of a hardcoded list) means we only ever
 * offer documents the backend actually configures for that state + type
 * (agriculture vs non-agriculture). Returns [] on any failure so the panel
 * degrades quietly.
 */
export async function fetchPropertyDocsCatalogue(stateId, mode) {
  if (!stateId || !mode) return [];
  try {
    const res = await AxiosPropertyInstance.get(
      `static/states/${stateId}/${mode}/property/docs.json`
    );
    return res?.data?.docs ?? [];
  } catch {
    return [];
  }
}

/**
 * The state SERVICE catalogue (states/<state>/<mode>/index.json) — a plain array
 * whose entries carry `logo`/`mobileLogo` (the report preview images), which
 * docs.json does not. Used to resolve a doc's preview for the buyer audit modal.
 * Returns [] on failure.
 */
export async function fetchStateServiceCatalogue(stateId, mode) {
  if (!stateId || !mode) return [];
  try {
    const res = await AxiosPropertyInstance.get(
      `static/states/${stateId}/${mode}/index.json`
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/** The single service-catalogue entry for a doc (with its `logo`), or null. */
export async function fetchServiceDoc(stateId, mode, docId) {
  const cat = await fetchStateServiceCatalogue(stateId, mode);
  return cat.find((d) => d && d.id === docId) || null;
}

/**
 * Post a buyer/tenant requirement ("Looking for something?" demand lead).
 * Optional auth — a logged-in user is stamped server-side; an anonymous lead
 * must include `contact.phone`. Returns `{ id, message }`.
 */
export async function createPropertyRequest(payload) {
  const res = await AxiosPropertyInstance.post(`/requests`, payload, authHeader());
  return res?.data;
}

/**
 * The states catalogue (`[{ id, name, ... }]`) — used by the standalone Audit
 * Report picker (no property context). Returns [] on failure.
 */
export async function fetchStates() {
  try {
    const res = await AxiosPropertyInstance.get(`static/states/index.json`);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * Save / unsave a listing to the user's wishlist (mirrors the app's wishlist
 * endpoints). `listedFor` is required by the server on add.
 */
export async function addWishlist(propertyId, listedFor) {
  const res = await AxiosPropertyInstance.post(
    `/wishlist/add`,
    { propertyId, listedFor: listedFor || "sell" },
    authHeader()
  );
  return res?.data;
}
export async function removeWishlist(propertyId) {
  const res = await AxiosPropertyInstance.delete(
    `/wishlist/removeWishList?propertyId=${propertyId}`,
    authHeader()
  );
  return res?.data;
}

/**
 * Like / unlike a listing — the PUBLIC popularity signal, separate from the
 * private wishlist above. Drives the like count on cards + the "Most liked" sort.
 */
export async function addLike(propertyId) {
  const res = await AxiosPropertyInstance.post(
    `/likes/add`,
    { propertyId },
    authHeader()
  );
  return res?.data;
}
export async function removeLike(propertyId) {
  const res = await AxiosPropertyInstance.delete(`/likes/remove`, {
    ...authHeader(),
    data: { propertyId },
  });
  return res?.data;
}

/**
 * The signed-in user's liked property ids (as strings), to light up the like
 * buttons. Returns [] when logged out / on failure.
 */
export async function fetchLikedIds() {
  try {
    const res = await AxiosPropertyInstance.post(`/likes/my`, {}, authHeader());
    const ids = res?.data?.data?.item?.propertyIds ?? [];
    return (Array.isArray(ids) ? ids : []).map(String);
  } catch {
    return [];
  }
}

/**
 * The signed-in user's wishlisted property ids (as strings). Returns [] when
 * logged out or on failure — the heart just shows empty.
 */
export async function fetchWishlistIds() {
  try {
    const res = await AxiosPropertyInstance.post(
      `/wishlist/userWishList`,
      { filter: {} },
      authHeader()
    );
    const items = res?.data?.data?.items ?? res?.data?.data ?? [];
    return (Array.isArray(items) ? items : [])
      .map((w) => w?.properties?._id || w?.propertyId || w?._id)
      .filter(Boolean)
      .map(String);
  } catch {
    return [];
  }
}

/**
 * The user's saved (wishlisted) listings as full property items — powers the
 * "Saved" scope. Each wishlist row carries the property under `properties` plus
 * a top-level `verifiedCount`. Returns [] when logged out / on failure.
 */
export async function fetchWishlist() {
  try {
    const res = await AxiosPropertyInstance.post(
      `/wishlist/userWishList`,
      { filter: {} },
      authHeader()
    );
    const rows = res?.data?.data?.items ?? res?.data?.data ?? [];
    return (Array.isArray(rows) ? rows : [])
      .map((w) =>
        w?.properties
          ? { ...w.properties, verifiedCount: w.verifiedCount ?? w.properties.verifiedCount }
          : w
      )
      .filter((p) => p && p._id);
  } catch {
    return [];
  }
}

/**
 * Ping a property's view counter (best-effort; never blocks the UI).
 */
export async function bumpView(id, name = "") {
  try {
    await AxiosPropertyInstance.post(`/${id}/view`, { name }, authHeader());
  } catch {
    /* ignore */
  }
}

/**
 * List a property (create or update its listing). Mirrors the app's `addtoListing`
 * thunk EXACTLY: PUT `/{_id}` with `demand:"medium"` injected and `_id`/`state`
 * stripped from the body. `payload` must be `{ listings, propertyArea, _id,
 * listed:"true", listedFor }` — `propertyArea` stays top-level, all other fields
 * live under `listings`.
 */
export async function submitListing(payload) {
  const data = { ...payload, demand: "medium" };
  const id = data._id;
  delete data._id;
  delete data.state;
  const res = await AxiosPropertyInstance.put(`/${id}`, data, authHeader());
  return res?.data;
}

/**
 * Transfer a property to another user by their phone number — mirrors the mobile
 * `transferPropertyService`: POST `/transferProperty` with
 * `{ propertyId, mobileNo, countryCode }`. On success the property leaves the
 * caller's account. Returns the raw response (`status.message` carries the
 * server's human message); throws on failure so the caller can surface it.
 */
export async function transferProperty(propertyId, mobileNo, countryCode) {
  const res = await AxiosPropertyInstance.post(
    `/transferProperty`,
    { propertyId, mobileNo, countryCode },
    authHeader()
  );
  return res?.data;
}

/**
 * Upload a document file onto a property's doc slot (owner only) — mirrors the
 * My-Properties Documents-tab upload exactly: multipart `PUT /{propertyId}/docs/
 * {docId}` with the file under `files` and the slot id under `key`. On success
 * the file attaches to `property.docs[docId]` and enters the verification
 * pipeline (the same store the listing's `verifiedCount` reads). Returns raw data.
 */
export async function uploadListingDoc(propertyId, docId, file) {
  const form = new FormData();
  form.append("files", file);
  form.append("key", docId);
  const res = await AxiosPropertyInstance.put(
    `/${propertyId}/docs/${docId}`,
    form,
    authHeader()
  );
  return res?.data;
}

/**
 * Upload one or more photos onto the property (owner only) — the exact
 * My-Properties gallery upload: multipart `POST /{propertyId}/photo` with each
 * file under `propertyPic`. Returns raw data; refetch the listing to see them.
 */
export async function uploadListingImages(propertyId, files) {
  const form = new FormData();
  // Only real files — guards the off-by-one that the My-Properties handler has
  // (it appends one `undefined`).
  (files || []).filter(Boolean).forEach((f) => form.append("propertyPic", f));
  const res = await AxiosPropertyInstance.post(
    `/${propertyId}/photo`,
    form,
    authHeader()
  );
  return res?.data;
}

/**
 * Delete an uploaded listing photo (owner only) — `DELETE /{propertyId}/photo`
 * with the raw image object. The endpoint validates each item as exactly
 * `{ _id, path, createdAt }` (rejects unknown fields) — it uses `path` to drop
 * the S3 object and `_id` to `$pull` the sub-doc — so send just those three.
 */
export async function deleteListingImage(propertyId, image) {
  const item = {
    _id: image?._id,
    path: image?.path,
    createdAt: image?.createdAt,
  };
  const res = await AxiosPropertyInstance.delete(`/${propertyId}/photo`, {
    ...authHeader(),
    data: { images: [item] },
  });
  return res?.data;
}

/**
 * Verify an uploaded document (owner only) — the exact 5-credit "Verify Now"
 * action from the My-Properties Documents tab: POST the VERIFY_DOCUMENT checkout
 * with the doc + the property's STATE + location. On success the slot moves
 * NOT_VERIFIED → IN_PROGRESS (→ VERIFIED once reviewed). Sent with
 * `payShortfall` like My Properties: a short wallet comes back as a Cashfree
 * checkout ({ sessionId, orderId, ... }) for the rest instead of an
 * "insufficient credits" error. Throws on other failures.
 */
export async function verifyListingDoc({ propertyId, docId, docName, state, location }) {
  const body = {
    propertyId,
    documentId: docId,
    document: { id: docId, name: docName },
    state,
    property: { location },
    payShortfall: true,
  };
  const res = await AxiosPaymentInstance.post(
    `/v1/customer/checkout/type/VERIFY_DOCUMENT`,
    body,
    authHeader()
  );
  return res?.data?.data?.item ?? null;
}

/**
 * Unlist a property: PUT `/{id}` with `listed:false`. We deliberately do NOT
 * reset `listedFor` to "own" any more — preserving its sell/rent value is what
 * lets "My listings" keep showing it (via includeUnlisted) with a Relist action,
 * and keeps it OUT of the never-listed picker pool. Reversible; does not delete.
 */
export async function unlistProperty(id) {
  const res = await AxiosPropertyInstance.put(
    `/${id}`,
    { listed: false },
    authHeader()
  );
  return res?.data;
}

/**
 * Relist a previously-unlisted property: PUT `/{id}` with `listed:true` and the
 * original `listedFor` (sell/rent). The inverse of unlistProperty.
 */
export async function relistProperty(id, listedFor) {
  const res = await AxiosPropertyInstance.put(
    `/${id}`,
    { listed: true, listedFor: listedFor === "rent" ? "rent" : "sell" },
    authHeader()
  );
  return res?.data;
}

/**
 * Crowd market-price contributions for a listing. The summary is public:
 * { property:{avg,count,...}, area:{avg,count,name}, mine, unit }. Returns null
 * on failure so the section can just hide.
 */
export async function fetchPriceSummary(propertyId) {
  try {
    const res = await AxiosPropertyInstance.get(
      // Rates are per-area (₹/sq ft); the summary must query the same unit it's
      // stored under, else it wouldn't find the contributions.
      `/price-contributions/${propertyId}?unit=per_area`,
      authHeader()
    );
    return res?.data?.data?.item || null;
  } catch {
    return null;
  }
}

/** Submit a market-rate opinion (₹ per sq ft) for a listing (login required).
 *  Returns the fresh summary. */
export async function submitPriceContribution(propertyId, value, note = "") {
  const res = await AxiosPropertyInstance.post(
    `/price-contributions`,
    { propertyId, value, note, unit: "per_area" },
    authHeader()
  );
  return res?.data?.data?.item || null;
}

// ── Bank auctions (IBAPI + bankeauctions.com + bankauctions.in proxy) ─────────
/** A page of bank-auction listings. `params`: { source (csv), city, category (csv),
 *  q, minPrice, maxPrice, sort, page, limit }. Returns { items, count, pagination }. */
export async function fetchAuctions(params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  );
  const res = await axios.get(AUCTIONS_BASE, { ...authHeader(), params: clean });
  return {
    items: res?.data?.data?.items ?? [],
    count: res?.data?.data?.count ?? 0,
    pagination: res?.data?.pagination ?? null,
  };
}

/** Filter facets: { total, cities:[{name,count,lat,lng}],
 *  categories:[{key,label,count}], sources:[{key,label,count}],
 *  priceRange:{min,max} }. `source` (csv) scopes city/category/price/total counts
 *  to the selected source(s); the `sources` list stays global. */
export async function fetchAuctionFacets(params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  );
  const res = await axios.get(`${AUCTIONS_BASE}/facets`, {
    ...authHeader(),
    params: clean,
  });
  return (
    res?.data?.data?.item ?? {
      total: 0,
      cities: [],
      categories: [],
      sources: [],
      priceRange: { min: 0, max: 0 },
    }
  );
}

/** Rich, on-demand detail for one auction (id = "<source>:<rawId>"). Only IBAPI
 *  returns the extended payload (borrower, owner, pincode, ownership, area,
 *  coordinates, auction officer); other sources echo their list row. */
export async function fetchAuctionDetail(id) {
  const res = await axios.get(`${AUCTIONS_BASE}/detail`, {
    ...authHeader(),
    params: { id },
  });
  return res?.data?.data?.item ?? null;
}
